import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {DescargaService} from '@shared/services/descarga.service';
import {HttpCommonService} from '@shared/services/http-common.service';
import {GenericHttpResponse} from '@shared/services/interfaces/generic-http-response.interface';
import {PresupuestoCliente, PresupuestoClienteFiltros, PresupuestoClientePaginado} from './presupuestos.interface';

/**
 * Presupuestos cargados directamente sobre el cliente.
 *
 * No confundir con `PresupuestosService` (crm/presupuestos), que sigue
 * apuntando a `/clie/serviciosPresupuestos`: aquellos cuelgan de un servicio
 * solicitado en un establecimiento y quedaron sin UI.
 */
@Injectable({
  providedIn: 'root'
})
export class PresupuestosClienteService {
  private http = inject(HttpClient);
  private httpCommonService = inject(HttpCommonService);
  private descargaService = inject(DescargaService);

  index(
    clienteId: number,
    page: number = 1,
    perPage: number = 15,
    filtros?: PresupuestoClienteFiltros
  ): Observable<GenericHttpResponse<PresupuestoClientePaginado>> {
    const url = this.httpCommonService.makeUrl('/clie/presupuestos');

    let params = new HttpParams()
      .set('cli_cliente_id', clienteId.toString())
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (filtros) {
      if (filtros.busqueda?.trim()) {
        params = params.set('busqueda', filtros.busqueda.trim());
      }
      if (filtros.fecha_desde?.trim()) {
        params = params.set('fecha_desde', filtros.fecha_desde.trim());
      }
      if (filtros.fecha_hasta?.trim()) {
        params = params.set('fecha_hasta', filtros.fecha_hasta.trim());
      }
    }

    return this.http.get<GenericHttpResponse<PresupuestoClientePaginado>>(url, {params});
  }

  store(body: FormData): Observable<GenericHttpResponse<PresupuestoCliente>> {
    const url = this.httpCommonService.makeUrl('/clie/presupuestos');
    return this.http.post<GenericHttpResponse<PresupuestoCliente>>(url, body);
  }

  /**
   * Va por POST con `_method=PUT` porque el payload es `FormData`: PHP no parsea
   * multipart en un PUT real.
   */
  update(id: number, body: FormData): Observable<GenericHttpResponse<PresupuestoCliente>> {
    const url = this.httpCommonService.makeUrl(`/clie/presupuestos/${id}`);
    return this.http.post<GenericHttpResponse<PresupuestoCliente>>(url, body);
  }

  destroy(id: number): Observable<GenericHttpResponse<PresupuestoCliente>> {
    const url = this.httpCommonService.makeUrl(`/clie/presupuestos/${id}`);
    return this.http.delete<GenericHttpResponse<PresupuestoCliente>>(url);
  }

  descargar(id: number, nombre: string): Observable<string> {
    const url = this.httpCommonService.makeUrl(`/clie/presupuestos/${id}/descargar`);
    return this.descargaService.descargar(url, nombre);
  }
}
